/**
 * `AIConversationPresentationModel.ts` — port of Unity's
 * `AIConversationPresentationModel.cs`.
 *
 * Presentation-only queue for tool cards, `present_options` rounds, guardrail
 * notices, and document CITATIONS. It is NOT the chat transcript — the panel
 * owns its history entries (mirroring Unity's
 * `AIPanel.ConversationHistoryEntry`).
 */

import { Emitter } from "@core/events/emitter";
import type { AIFileCitation } from "@ai/context/AIContextEngineModel.KnowledgeTransport";

export interface AIToolExecutionPresentation {
  readonly entryId: number;
  readonly toolName: string;
  readonly source: string;
  readonly result: string | null;
}

export interface AIPresentOptionsRound {
  readonly question: string;
  readonly options: ReadonlyArray<string>;
}

export interface AIPresentOptionsPresentation {
  readonly entryId: number;
  readonly rounds: ReadonlyArray<AIPresentOptionsRound>;
  readonly moreInfoRequired: boolean;
}

export interface AIGuardrailNoticePresentation {
  readonly entryId: number;
  readonly message: string;
  readonly severity: "info" | "warning" | "error";
}

/**
 * One document source surfaced with an answer — the web equivalent of Unity's
 * `PublishCitations(IReadOnlyList<AIFileCitation>)`.
 */
export interface AICitationPresentation {
  readonly entryId: number;
  readonly fileId: string;
  readonly fileName: string | null;
  /** `docs/{fileName}` — what `IProjectDocsModel.openAsync` takes. */
  readonly storagePath: string | null;
}

/**
 * A source line under an answer — where a figure came from. The counterpart of
 * the citation chips, for facts that were MEASURED rather than looked up in a
 * document.
 *
 * It exists because the realtime session runs `output_modalities: ["audio"]`,
 * so everything the model writes is spoken: the Answer skill's
 * `Measured in the scene` attribution has to be published by the tool that
 * produced the figure, or Spark reads "Source: Measured in the scene" aloud
 * after every measurement.
 */
export interface AIAttributionPresentation {
  readonly entryId: number;
  readonly text: string;
}

/** What a knowledge tool actually searched, for the debug tool card. */
export interface AIToolUsageAnnotation {
  readonly prompt?: string;
  /** `"string search"` | `"semantic search"` | `"llm"` (Unity's literals). */
  readonly source?: string;
}

export class AIConversationPresentationModel {
  readonly onToolExecutionsChanged = new Emitter<void>();
  readonly onPresentOptionsChanged = new Emitter<void>();
  readonly onGuardrailNoticesChanged = new Emitter<void>();
  readonly onCitationsChanged = new Emitter<void>();
  readonly onAttributionsChanged = new Emitter<void>();
  /**
   * A tool asked the UI to OPEN a document (Unity
   * `RequestCitationOpen` — the `Find_and_Show_PDF` path, which is the same
   * action as the user clicking a citation chip).
   */
  readonly onCitationOpenRequested = new Emitter<AICitationPresentation>();

  private m_nextEntryId = 1;
  private m_pendingToolExecutions: AIToolExecutionPresentation[] = [];
  private m_pendingOptions: AIPresentOptionsPresentation[] = [];
  private m_pendingNotices: AIGuardrailNoticePresentation[] = [];
  private m_pendingCitations: AICitationPresentation[] = [];
  private m_pendingAttributions: AIAttributionPresentation[] = [];
  /** toolName → what it searched; drained by the tool card. */
  private readonly m_toolUsage = new Map<string, AIToolUsageAnnotation>();

  publishToolExecution(toolName: string, source: string, result: string | null): void {
    this.m_pendingToolExecutions.push({
      entryId: this.m_nextEntryId++,
      toolName,
      source,
      result,
    });
    this.onToolExecutionsChanged.emit();
  }

  publishPresentOptions(
    rounds: ReadonlyArray<AIPresentOptionsRound>,
    moreInfoRequired: boolean,
  ): void {
    this.m_pendingOptions.push({
      entryId: this.m_nextEntryId++,
      rounds,
      moreInfoRequired,
    });
    this.onPresentOptionsChanged.emit();
  }

  publishGuardrailNotice(message: string, severity: "info" | "warning" | "error"): void {
    this.m_pendingNotices.push({ entryId: this.m_nextEntryId++, message, severity });
    this.onGuardrailNoticesChanged.emit();
  }

  /** Drain queues — consumers pull pending entries exactly once. */
  consumePendingToolExecutions(): ReadonlyArray<AIToolExecutionPresentation> {
    const pending = this.m_pendingToolExecutions;
    this.m_pendingToolExecutions = [];
    return pending;
  }

  consumePendingPresentOptions(): ReadonlyArray<AIPresentOptionsPresentation> {
    const pending = this.m_pendingOptions;
    this.m_pendingOptions = [];
    return pending;
  }

  consumePendingGuardrailNotices(): ReadonlyArray<AIGuardrailNoticePresentation> {
    const pending = this.m_pendingNotices;
    this.m_pendingNotices = [];
    return pending;
  }

  // ---- Citations (documents) ----------------------------------------

  /** Unity `PublishCitations` — de-duplicated by storage path / file id. */
  publishCitations(citations: ReadonlyArray<AIFileCitation>): void {
    const seen = new Set(
      this.m_pendingCitations.map((c) => (c.storagePath ?? c.fileId).toLowerCase()),
    );
    let added = false;
    for (const citation of citations) {
      const key = (citation.storagePath ?? citation.fileId).toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      this.m_pendingCitations.push({
        entryId: this.m_nextEntryId++,
        fileId: citation.fileId,
        fileName: citation.fileName,
        storagePath: citation.storagePath,
      });
      added = true;
    }
    if (added) this.onCitationsChanged.emit();
  }

  consumePendingCitations(): ReadonlyArray<AICitationPresentation> {
    const pending = this.m_pendingCitations;
    this.m_pendingCitations = [];
    return pending;
  }

  // ---- Attribution (measured facts) ---------------------------------

  /**
   * Publish the source line for the answer being composed. De-duplicated by
   * text, because one turn can measure several parts and repeating the same
   * line per part says nothing extra.
   */
  publishAttribution(text: string): void {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (this.m_pendingAttributions.some((a) => a.text === trimmed)) return;
    this.m_pendingAttributions.push({ entryId: this.m_nextEntryId++, text: trimmed });
    this.onAttributionsChanged.emit();
  }

  consumePendingAttributions(): ReadonlyArray<AIAttributionPresentation> {
    const pending = this.m_pendingAttributions;
    this.m_pendingAttributions = [];
    return pending;
  }

  // ---- Citations (documents), continued ------------------------------

  /** Unity `RequestCitationOpen` — `Find_and_Show_PDF` opens without a click. */
  requestCitationOpen(citation: AIFileCitation): void {
    this.onCitationOpenRequested.emit({
      entryId: this.m_nextEntryId++,
      fileId: citation.fileId,
      fileName: citation.fileName,
      storagePath: citation.storagePath,
    });
  }

  // ---- Tool usage annotations ---------------------------------------

  /** Unity `PublishToolUsagePrompt` — the composed prompt a tool searched with. */
  publishToolUsagePrompt(toolName: string, prompt: string): void {
    this.m_toolUsage.set(toolName, { ...this.m_toolUsage.get(toolName), prompt });
  }

  /** Unity `PublishToolUsageSource` — `"string search"` / `"semantic search"` / `"llm"`. */
  publishToolUsageSource(toolName: string, source: string): void {
    this.m_toolUsage.set(toolName, { ...this.m_toolUsage.get(toolName), source });
  }

  /** Read-and-clear, so a later run of the same tool can't show a stale note. */
  takeToolUsage(toolName: string): AIToolUsageAnnotation | null {
    const usage = this.m_toolUsage.get(toolName) ?? null;
    this.m_toolUsage.delete(toolName);
    return usage;
  }
}
